import React, { useState } from "react";
import { Scale, ChevronRight, Loader2 } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { useNavigate } from "react-router-dom";
import { aiService } from "../services/aiService";

const LegalSearchBlock: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    "arrest without warrant maryland"
  );
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const searchSuggestions = [
    "arrest without warrant maryland",
    "domestic violence",
    "consent to search",
    "vehicle stop",
    "Miranda rights",
    "deadly force",
  ];

  const handleSearch = async () => {
    const query = searchQuery.trim() || "arrest without warrant maryland";
    setIsLoading(true);
    
    try {
      const response = await aiService.chat({
        message: `Please provide a comprehensive legal analysis for: "${query}". Include relevant Maryland state laws, federal case law, and practical law enforcement guidance.`,
        context: "Legal research for law enforcement professionals",
        userId: "legal-search-user"
      });
      
      // Navigate to search results with the AI response
      navigate("/legal-search-results", { 
        state: { 
          query,
          aiResponse: response 
        } 
      });
    } catch (error) {
      console.error("Search error:", error);
      // Fallback to static results if API fails
      navigate(`/legal-search-results?q=${encodeURIComponent(query)}`);
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  return (
    <>
      <Card
        className="bg-slate-800 border-l-4 border-command-light-blue rounded-xl cursor-pointer hover:bg-slate-700 transition-colors"
        onClick={() => setIsOpen(true)}
      >
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Row 1: Icon + Title + Description */}
            <div className="flex items-start space-x-4">
              <div className="bg-command-light-blue/30 p-2 rounded-full mt-3">
                <Scale className="h-8 w-8 text-command-light-blue/90" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white">Legal Search</h3>
                <p className="text-white/70 text-sm">Find the state laws that apply to your scene.</p>
              </div>
            </div>

            {/* Row 2: copy cleanup (placeholders removed) */}
          </div>
        </CardContent>
      </Card>

      {/* Custom Dialog */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/80 z-50"
            onClick={() => setIsOpen(false)}
          />

          {/* Dialog */}
          <div className="fixed left-10 right-10 top-1/2 transform -translate-y-1/2 z-50 bg-slate-800 border border-slate-600 rounded-xl shadow-2xl max-w-md mx-auto">
            {/* Header */}
            <div className="p-6 pb-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-command-light-blue/30 p-2 rounded-full">
                  <Scale className="h-6 w-6 text-command-light-blue" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">
                    Legal Search
                  </h2>
                  <p className="text-slate-300 text-sm">
                    Search 16 legal authorities with NLP.
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 pb-6 space-y-4">
              {/* Search Input */}
              <div>
                <input
                  type="text"
                  placeholder="arrest without warrant maryland"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-md text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-command-light-blue/50"
                />
              </div>

              {/* Search Suggestions */}
              <div>
                <p className="text-slate-300/70 text-sm mb-1">
                  NLP-enhanced search suggestions:
                </p>
                <div className="flex flex-wrap gap-2">
                  {searchSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setSearchQuery(suggestion)}
                      className="px-3 py-1 bg-slate-600 hover:bg-slate-500 text-slate-200 text-xs rounded-full transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSearch}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-command-light-blue hover:bg-command-light-blue/80 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md transition-colors flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Searching...
                    </>
                  ) : (
                    "Search"
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default LegalSearchBlock;
